import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { createShare, addSharedWithMe, getShareRecipientCount, uploadShareNda, updateShare } from '../../services/shareService';
import { getMyFilesOnce } from '../../services/myFilesService';
import { getUidByEmail, normalizeEmail } from '../../services/userEmailService';
import { queueShareLinkEmail } from '../../services/shareLinkEmailService';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import ShareModalVipPanel from './ShareModalVipPanel';
import { X, Copy, Share2, FileText } from 'lucide-react';

const FACEBOOK_SHARER = 'https://www.facebook.com/sharer/sharer.php';

function openFacebookShare(url) {
  const u = encodeURIComponent(url);
  window.open(`${FACEBOOK_SHARER}?u=${u}`, 'facebook-share', 'width=580,height=400');
}

const ShareModal = ({
  propertyId,
  onClose,
  onShared,
  showPocketVip = false,
  /** Affiche « Autres options » (cession, NDA, anonymisation) : courtiers ou propriétaire sur son immeuble. */
  showBrokerShareOptions = false,
  /**
   * Si true, le document `shares` a `sharedByBroker: true` (parcours courtier).
   * Un investisseur propriétaire peut activer les mêmes options sans ce flag.
   */
  shareRecordedAsBroker = false,
  initialShareTab = 'email',
  vipDraft = null,
  /** Depuis « Renvoyer » (campagnes pocket) : pas d’onglets courriel / lien, seulement VIP. */
  onlyVipShareTab = false,
  /** Immeuble avec mandat de vente déclaré à l’analyse — le partage mandat est implicite si module actif. */
  propertyBrokerSaleMandate = false,
  /** users.modules.purchaseOffersEnabled (courtier immo). */
  purchaseOffersModuleEnabled = false,
  /** Immeuble détenu : lecture seule, pas de cession/mandat, NDA indépendant, sync live. */
  isOwnedDeal = false,
}) => {
  const { currentUser, userProfile } = useAuth();
  const { t, language } = useLanguage();
  const [shareTab, setShareTab] = useState(initialShareTab);
  const [email, setEmail] = useState('');
  const [access, setAccess] = useState('read');
  const [allowSubScenariosEdit, setAllowSubScenariosEdit] = useState(false);
  const [shareForCession, setShareForCession] = useState(false);
  const [shareForMandatedSale, setShareForMandatedSale] = useState(false);
  const [anonymizeAddress, setAnonymizeAddress] = useState(false);
  const [includeNda, setIncludeNda] = useState(false);
  const [ndaFile, setNdaFile] = useState(null);
  const [myFilesList, setMyFilesList] = useState([]);
  const [selectedMyFileRef, setSelectedMyFileRef] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [linkCreated, setLinkCreated] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);
  const emailInputRef = useRef(null);
  const ndaStateRef = useRef({ ndaFile: null, selectedMyFileRef: null });

  useEffect(() => {
    ndaStateRef.current = { ndaFile, selectedMyFileRef };
  }, [ndaFile, selectedMyFileRef]);

  const ndaFileRefForShare = selectedMyFileRef
    ? { storagePath: selectedMyFileRef.storagePath, name: selectedMyFileRef.name }
    : null;

  const brokerMandatedSaleOnShare =
    !isOwnedDeal &&
    !!purchaseOffersModuleEnabled &&
    !shareForCession &&
    ((!!propertyBrokerSaleMandate && !!shareRecordedAsBroker) ||
      (!!showBrokerShareOptions && !!shareRecordedAsBroker && !!shareForMandatedSale));

  const ndaAttachedOnShare =
    showBrokerShareOptions &&
    includeNda &&
    (isOwnedDeal || shareForCession) &&
    ndaFileRefForShare?.storagePath
      ? ndaFileRefForShare
      : null;

  const ownedDiligenceOnShare =
    isOwnedDeal &&
    showBrokerShareOptions &&
    (anonymizeAddress || includeNda);

  const buildShareOpts = () => ({
    propertyId,
    access: isOwnedDeal ? 'read' : access,
    allowSubScenariosEdit: isOwnedDeal ? false : allowSubScenariosEdit,
    openToCession: isOwnedDeal ? false : showBrokerShareOptions ? shareForCession : false,
    anonymizeAddress: showBrokerShareOptions ? anonymizeAddress : false,
    ndaFileRef: ndaAttachedOnShare,
    sharedByBroker: shareRecordedAsBroker,
    brokerMandatedSale: brokerMandatedSaleOnShare,
    purchaseOffersModuleActive: !!purchaseOffersModuleEnabled,
    liveSync: !!isOwnedDeal,
    ownedDiligence: !!ownedDiligenceOnShare,
  });

  const shareOpts = buildShareOpts;
  const getVipShareOpts = buildShareOpts;

  const shouldAttachNdaUpload = () =>
    showBrokerShareOptions && includeNda && (isOwnedDeal || shareForCession);

  const validateBrokerShareOptions = () => {
    if (!showBrokerShareOptions) return true;
    if (!isOwnedDeal && shareForMandatedSale && !purchaseOffersModuleEnabled) {
      setError(t('shareModal.mandatedSaleRequiresModule'));
      return false;
    }
    if (shouldAttachNdaUpload() && !ndaFile && !selectedMyFileRef?.storagePath) {
      setError(t('shareModal.ndaRequired'));
      return false;
    }
    return true;
  };

  const attachNdaToShare = async (token) => {
    if (ndaFileRefForShare) return;
    if (!ndaFile) return;
    const fileRef = await uploadShareNda(token, ndaFile);
    await updateShare(token, { ndaFileRef: fileRef });
  };

  const showVipOnly = onlyVipShareTab && showPocketVip;

  useEffect(() => {
    setShareTab(showVipOnly ? 'vip' : initialShareTab);
  }, [propertyId, initialShareTab, showVipOnly]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shareTab === 'email') emailInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [shareTab]);

  useEffect(() => {
    if (!currentUser?.uid || !propertyId) return;
    getMyFilesOnce(currentUser.uid).then(setMyFilesList).catch(() => setMyFilesList([]));
  }, [currentUser?.uid, propertyId]);

  useEffect(() => {
    if (!linkCreated?.token) return;
    getShareRecipientCount(linkCreated.token).then(setRecipientCount).catch(() => setRecipientCount(0));
  }, [linkCreated?.token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalized = normalizeEmail(email);
    if (!normalized) {
      setError(t('shareModal.emailRequired'));
      return;
    }
    if (!validateBrokerShareOptions()) return;
    if (!currentUser?.uid || !propertyId) return;
    setLoading(true);
    setError('');
    setSuccess(null);
    try {
      const { token, url, snapshot } = await createShare(currentUser.uid, shareOpts());
      if (shouldAttachNdaUpload() && ndaFile) await attachNdaToShare(token);
      const recipientUid = await getUidByEmail(normalized);
      const previewAnonymize = showBrokerShareOptions && anonymizeAddress;
      if (recipientUid) {
        await addSharedWithMe(recipientUid, currentUser.uid, token, snapshot, {
          anonymizeAddress: previewAnonymize,
        });
        setSuccess(t('shareModal.sentToAccount').replace('{email}', normalized));
      } else {
        await queueShareLinkEmail({
          to: normalized,
          shareLink: url,
          senderName: userProfile?.displayName || currentUser?.displayName || currentUser?.email || '',
        });
        setSuccess(t('shareModal.sentByEmail').replace('{email}', normalized));
      }
      onShared?.(propertyId);
      setTimeout(() => onClose?.(), 2500);
    } catch (e) {
      setError(e?.message || t('share.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async () => {
    if (!currentUser?.uid || !propertyId) return;
    if (!validateBrokerShareOptions()) return;
    setLoading(true);
    setError('');
    setLinkCreated(null);
    try {
      const { token, url } = await createShare(currentUser.uid, shareOpts());
      if (shouldAttachNdaUpload() && ndaFile) await attachNdaToShare(token);
      setLinkCreated({ token, url });
      onShared?.(propertyId);
    } catch (e) {
      setError(e?.message || t('share.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!linkCreated?.url) return;
    navigator.clipboard.writeText(linkCreated.url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const recipientCountText =
    recipientCount != null
      ? (recipientCount === 1
          ? t('shareModal.recipientCount').replace('{count}', recipientCount)
          : (t('shareModal.recipientCount_plural') || t('shareModal.recipientCount')).replace('{count}', recipientCount))
      : null;

  const ndaFilePickerBlock = (
    <div className="space-y-2">
      {myFilesList.length > 0 && (
        <div>
          <span className="text-xs text-gray-500 block mb-1">{t('myFiles.chooseFromList')}</span>
          <select
            value={selectedMyFileRef?.id ?? ''}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) {
                setSelectedMyFileRef(null);
                return;
              }
              const f = myFilesList.find((x) => x.id === id);
              if (f) {
                setSelectedMyFileRef(f);
                setNdaFile(null);
              }
            }}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
          >
            <option value="">—</option>
            {myFilesList.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label || f.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer text-sm bg-white">
          <FileText className="w-4 h-4 text-gray-600" />
          {ndaFile ? ndaFile.name : t('shareModal.chooseNdaFile')}
          <input
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setNdaFile(f);
                setSelectedMyFileRef(null);
              }
              e.target.value = '';
            }}
          />
        </label>
        {(ndaFile || selectedMyFileRef) && (
          <button
            type="button"
            onClick={() => {
              setNdaFile(null);
              setSelectedMyFileRef(null);
            }}
            className="text-sm text-red-600 hover:underline"
          >
            {t('shareModal.removeNda')}
          </button>
        )}
      </div>
    </div>
  );

  const brokerShareOptionsBlock = showBrokerShareOptions ? (
    <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3 space-y-3">
      <p className="text-xs font-semibold text-gray-800">{t('shareModal.brokerShareOptionsTitle')}</p>
      {isOwnedDeal && (
        <p className="text-xs text-gray-600">{t('shareModal.ownedHint')}</p>
      )}
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          checked={anonymizeAddress}
          onChange={(e) => setAnonymizeAddress(e.target.checked)}
        />
        <span className="text-sm text-gray-800">{t('shareModal.anonymizeAddress')}</span>
      </label>
      {!isOwnedDeal && propertyBrokerSaleMandate && purchaseOffersModuleEnabled && shareRecordedAsBroker ? (
        <p className="text-xs text-teal-900 bg-teal-50 border border-teal-100 rounded px-2 py-1.5">
          {t('shareModal.mandateFromPropertyNote')}
        </p>
      ) : null}
      {!isOwnedDeal && !propertyBrokerSaleMandate && purchaseOffersModuleEnabled && shareRecordedAsBroker ? (
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 shrink-0"
            checked={shareForMandatedSale}
            onChange={(e) => {
              const v = e.target.checked;
              setShareForMandatedSale(v);
              if (v) setShareForCession(false);
            }}
          />
          <span>
            <span className="text-sm font-medium text-gray-900 block">{t('shareModal.shareForMandatedSale')}</span>
            <span className="text-xs text-gray-600 block mt-0.5">{t('shareModal.shareForMandatedSaleHint')}</span>
          </span>
        </label>
      ) : null}
      {!isOwnedDeal && (
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 shrink-0"
            checked={shareForCession}
            onChange={(e) => {
              const v = e.target.checked;
              setShareForCession(v);
              if (v) setShareForMandatedSale(false);
              if (!v) {
                setIncludeNda(false);
                setNdaFile(null);
                setSelectedMyFileRef(null);
              }
            }}
          />
          <span>
            <span className="text-sm font-medium text-gray-900 block">{t('shareModal.shareForCession')}</span>
            <span className="text-xs text-gray-600 block mt-0.5">{t('shareModal.shareForCessionHint')}</span>
          </span>
        </label>
      )}
      {(isOwnedDeal || shareForCession) && (
        <div className={`${isOwnedDeal ? '' : 'pl-2 border-l-2 border-amber-200'} space-y-3`}>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={includeNda}
              onChange={(e) => {
                const v = e.target.checked;
                setIncludeNda(v);
                if (!v) {
                  setNdaFile(null);
                  setSelectedMyFileRef(null);
                }
              }}
            />
            <span>
              <span className="text-sm font-medium text-gray-900 block">{t('shareModal.includeNda')}</span>
              <span className="text-xs text-gray-600 block mt-0.5">
                {t(isOwnedDeal ? 'shareModal.includeNdaHintOwned' : 'shareModal.includeNdaHint')}
              </span>
            </span>
          </label>
          {includeNda && ndaFilePickerBlock}
        </div>
      )}
    </div>
  ) : null;

  useRegisterTopEscapeClose(onClose);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full min-w-0 p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold">{t('shareModal.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700"
            aria-label={t('cancel')}
          >
            <X size={20} />
          </button>
        </div>
        {success ? (
          <p className="text-green-600 text-sm">{success}</p>
        ) : linkCreated ? (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">{t('shareModal.shareLinkLabel')}</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                readOnly
                value={linkCreated.url}
                className="flex-1 min-w-0 text-sm border border-gray-300 rounded-lg px-3 py-2 bg-gray-50"
              />
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex items-center justify-center gap-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 shrink-0"
                title={t('shareModal.linkCopied')}
              >
                <Copy size={18} />
                {linkCopied ? '✓' : ''}
              </button>
            </div>
            <p className="text-sm text-gray-600">{t('shareModal.copyMessageForPost')}</p>
            <div className="flex gap-2">
              <p className="flex-1 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                {t('shareModal.facebookSuggestedMessage')}
                <br />
                <span className="text-blue-600 break-all">{linkCreated.url}</span>
              </p>
              <button
                type="button"
                onClick={() => {
                  const text = `${t('shareModal.facebookSuggestedMessage')}\n\n${linkCreated.url}`;
                  navigator.clipboard.writeText(text).then(() => {
                    setMessageCopied(true);
                    setTimeout(() => setMessageCopied(false), 2000);
                  });
                }}
                className="shrink-0 flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                title={t('shareModal.linkCopied')}
              >
                <Copy size={18} />
                {messageCopied ? '✓' : ''}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => openFacebookShare(linkCreated.url)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1877f2] text-white rounded-lg hover:opacity-90"
              >
                <Share2 size={18} />
                {t('shareModal.shareOnFacebook')}
              </button>
            </div>
            {recipientCountText != null && (
              <p className="text-sm text-gray-600">{recipientCountText}</p>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 border rounded-lg hover:bg-gray-50"
            >
              {t('close')}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-4">
              {isOwnedDeal ? (
                <p className="text-sm text-gray-600">{t('shareModal.ownedReadOnlyNote')}</p>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('shareModal.access')}
                  </label>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="access"
                        value="read"
                        checked={access === 'read'}
                        onChange={() => setAccess('read')}
                      />
                      {t('shareModal.accessRead')}
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="access"
                        value="write"
                        checked={access === 'write'}
                        onChange={() => setAccess('write')}
                      />
                      {t('shareModal.accessWrite')}
                    </label>
                  </div>
                  <label className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked={allowSubScenariosEdit}
                      onChange={(e) => setAllowSubScenariosEdit(e.target.checked)}
                    />
                    <span className="text-sm">{t('shareModal.allowSubScenarios')}</span>
                  </label>
                </>
              )}
              {brokerShareOptionsBlock}
            </div>

            {!showVipOnly && (
              <p className="text-sm text-gray-600 mb-3">{t('shareModal.sharedOptionsIntro')}</p>
            )}

            {!showVipOnly && (
            <div
              className="flex flex-wrap rounded-lg border border-gray-200 p-1 gap-1 mb-1"
              role="tablist"
              aria-label={t('shareModal.title')}
            >
              <button
                type="button"
                role="tab"
                aria-selected={shareTab === 'email'}
                onClick={() => setShareTab('email')}
                className={`flex-1 min-w-[7rem] py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                  shareTab === 'email'
                    ? 'bg-white text-blue-700 shadow-sm border border-gray-100'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {t('shareModal.tabEmail')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={shareTab === 'link'}
                onClick={() => setShareTab('link')}
                className={`flex-1 min-w-[7rem] py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                  shareTab === 'link'
                    ? 'bg-white text-blue-700 shadow-sm border border-gray-100'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {t('shareModal.tabLink')}
              </button>
              {showPocketVip && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={shareTab === 'vip'}
                  onClick={() => setShareTab('vip')}
                  className={`flex-1 min-w-[7rem] py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                    shareTab === 'vip'
                      ? 'bg-white text-violet-700 shadow-sm border border-gray-100'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {t('shareModal.tabVip')}
                </button>
              )}
            </div>
            )}

            {!showVipOnly && shareTab === 'email' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('shareModal.recipientEmail')}
                  </label>
                  <input
                    ref={emailInputRef}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="courriel@exemple.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    disabled={loading}
                  />
                </div>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? '…' : t('shareModal.send')}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            )}
            {!showVipOnly && shareTab === 'link' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">{t('shareModal.linkTabHint')}</p>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCreateLink}
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                  >
                    {loading ? '…' : t('shareModal.createLink')}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            )}
            {showPocketVip && (showVipOnly || shareTab === 'vip') && currentUser && (
              <div className="space-y-4">
                {error && <p className="text-red-600 text-sm">{error}</p>}
                {success && <p className="text-green-600 text-sm">{success}</p>}
                <ShareModalVipPanel
                  propertyId={propertyId}
                  currentUser={currentUser}
                  userProfile={userProfile}
                  language={language}
                  t={t}
                  shareForCession={shareForCession}
                  includeNda={includeNda}
                  requireNdaOnShare={shouldAttachNdaUpload()}
                  getVipShareOpts={getVipShareOpts}
                  ndaRef={ndaStateRef}
                  onShared={onShared}
                  onClose={onClose}
                  setSuccess={setSuccess}
                  setError={setError}
                  loading={loading}
                  setLoading={setLoading}
                  vipDraft={vipDraft}
                />
                <button type="button" onClick={onClose} className="w-full py-2 border rounded-lg hover:bg-gray-50">
                  {t('cancel')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
